import React, { useEffect, useState } from "react";
import { getUrl, list, remove, uploadData } from "aws-amplify/storage";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/AdminLayout.jsx";

const STORAGE_ROOT = "resolucion-de-libros/";
const PDF_JS_SOURCE =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
const PDF_JS_WORKER =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
const JPG_FILE = /\.jpe?g$/i;

let pdfJsPromise;

const isImageFile = (item) => item.size && JPG_FILE.test(item.path);

const getPdfJs = () => {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }

  if (!pdfJsPromise) {
    pdfJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = PDF_JS_SOURCE;
      script.async = true;
      script.onload = () => {
        if (!window.pdfjsLib) {
          reject(new Error("No se pudo cargar el lector de PDF."));
          return;
        }
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
        resolve(window.pdfjsLib);
      };
      script.onerror = () =>
        reject(new Error("No se pudo cargar el lector de PDF."));
      document.head.appendChild(script);
    });
  }

  return pdfJsPromise;
};

const toJpegBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo convertir una página a JPG."));
      },
      "image/jpeg",
      quality,
    );
  });

const normalizeFolderName = (name) => name.trim().replace(/[\\/]/g, "-");

async function getBookFiles() {
  const { items } = await list({
    path: STORAGE_ROOT,
    options: { listAll: true },
  });
  const booksByFolder = new Map();

  (items || []).filter(isImageFile).forEach((item) => {
    const [folder, type, fileName, ...rest] = item.path
      .slice(STORAGE_ROOT.length)
      .split("/");

    if (!folder || rest.length || !fileName) return;
    if (type !== "miniatura" && type !== "paginas") return;

    const book = booksByFolder.get(folder) || {
      name: folder,
      thumbnails: [],
      pages: [],
    };
    book[type === "miniatura" ? "thumbnails" : "pages"].push(item.path);
    booksByFolder.set(folder, book);
  });

  return Promise.all(
    [...booksByFolder.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(async (book) => {
        const [thumbnail] = book.thumbnails.sort((a, b) => a.localeCompare(b));

        if (!thumbnail || !book.pages.length) return null;

        const { url } = await getUrl({ path: thumbnail });
        return {
          name: book.name,
          imageUrl: url.href,
          pageCount: book.pages.length,
        };
      }),
  ).then((books) => books.filter(Boolean));
}

async function removeBookFiles(folder) {
  const { items } = await list({
    path: `${STORAGE_ROOT}${folder}/`,
    options: { listAll: true },
  });

  await Promise.all(
    (items || [])
      .filter((item) => item.size)
      .map((item) => remove({ path: item.path })),
  );
}

async function renderPdf(file, pageCount, onProgress) {
  const pdfjsLib = await getPdfJs();
  const loadingTask = pdfjsLib.getDocument({ data: await file.arrayBuffer() });
  const pdf = await loadingTask.promise;
  const pageNameWidth = Math.max(3, String(pageCount).length);

  try {
    const pages = [];
    let thumbnail;

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      onProgress(`Convirtiendo página ${pageNumber} de ${pageCount}...`);
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(2, 2000 / baseViewport.width);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: context, viewport }).promise;

      const image = await toJpegBlob(canvas, 0.9);
      pages.push({
        name: `${String(pageNumber).padStart(pageNameWidth, "0")}.jpg`,
        image,
      });

      if (pageNumber === 1) {
        const thumbnailScale = Math.min(1, 480 / baseViewport.width);
        const thumbnailViewport = page.getViewport({ scale: thumbnailScale });
        const thumbnailCanvas = document.createElement("canvas");
        const thumbnailContext = thumbnailCanvas.getContext("2d");

        thumbnailCanvas.width = Math.floor(thumbnailViewport.width);
        thumbnailCanvas.height = Math.floor(thumbnailViewport.height);
        await page.render({
          canvasContext: thumbnailContext,
          viewport: thumbnailViewport,
        }).promise;
        thumbnail = await toJpegBlob(thumbnailCanvas, 0.82);
      }
    }

    return { pages, thumbnail };
  } finally {
    await pdf.destroy();
  }
}

export default function ResolucionLibrosAdmin() {
  const [books, setBooks] = useState([]);
  const [bookName, setBookName] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPages, setSelectedPages] = useState(0);
  const [editingBook, setEditingBook] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  const fetchBooks = async () => {
    try {
      setLoading(true);
      setBooks(await getBookFiles());
    } catch (error) {
      console.error("Error al cargar resoluciones de libros:", error);
      alert("No se pudieron cargar las resoluciones de libros.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const resetForm = () => {
    setBookName("");
    setPdfFile(null);
    setTotalPages(0);
    setSelectedPages(0);
    setEditingBook(null);
    setProgress("");
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (book) => {
    setBookName(book.name);
    setPdfFile(null);
    setTotalPages(0);
    setSelectedPages(0);
    setEditingBook(book);
    setProgress("");
    setShowForm(true);
  };

  const handlePdfChange = async (event) => {
    const [file] = event.target.files;
    if (!file) return;
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      alert("Selecciona un archivo PDF.");
      return;
    }

    try {
      setProgress("Leyendo PDF...");
      const pdfjsLib = await getPdfJs();
      const loadingTask = pdfjsLib.getDocument({
        data: await file.arrayBuffer(),
      });
      const pdf = await loadingTask.promise;
      const pages = pdf.numPages;
      await pdf.destroy();

      setPdfFile(file);
      setTotalPages(pages);
      setSelectedPages(pages);
      setProgress(`El PDF contiene ${pages} páginas.`);
    } catch (error) {
      console.error("Error al leer el PDF:", error);
      setPdfFile(null);
      setTotalPages(0);
      setSelectedPages(0);
      setProgress("");
      alert("No se pudo leer el PDF. Verifica que no esté dañado o protegido.");
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const folder = normalizeFolderName(bookName);
    const requestedPages = Number(selectedPages);

    if (
      !folder ||
      !pdfFile ||
      !Number.isInteger(requestedPages) ||
      requestedPages < 1 ||
      requestedPages > totalPages
    ) {
      alert("Completa el nombre, el PDF y la cantidad de páginas a convertir.");
      return;
    }

    const duplicate = books.find(
      (book) =>
        book.name.localeCompare(folder, undefined, {
          sensitivity: "accent",
        }) === 0 && book.name !== editingBook?.name,
    );
    if (duplicate) {
      alert("Ya existe un libro con ese nombre.");
      return;
    }

    let previousImagesRemoved = false;

    try {
      setSaving(true);
      const convertedBook = await renderPdf(
        pdfFile,
        requestedPages,
        setProgress,
      );

      if (editingBook) {
        setProgress("Eliminando imágenes anteriores...");
        await removeBookFiles(editingBook.name);
        previousImagesRemoved = true;
      }

      const uploadedPaths = [];
      try {
        const thumbnailPath = `${STORAGE_ROOT}${folder}/miniatura/portada.jpg`;
        setProgress("Subiendo miniatura...");
        await uploadData({
          path: thumbnailPath,
          data: convertedBook.thumbnail,
          options: { contentType: "image/jpeg" },
        }).result;
        uploadedPaths.push(thumbnailPath);

        for (let index = 0; index < convertedBook.pages.length; index += 1) {
          const page = convertedBook.pages[index];
          const pagePath = `${STORAGE_ROOT}${folder}/paginas/${page.name}`;
          setProgress(
            `Subiendo página ${index + 1} de ${convertedBook.pages.length}...`,
          );
          await uploadData({
            path: pagePath,
            data: page.image,
            options: { contentType: "image/jpeg" },
          }).result;
          uploadedPaths.push(pagePath);
        }
      } catch (error) {
        await Promise.all(uploadedPaths.map((path) => remove({ path })));
        throw error;
      }

      resetForm();
      setShowForm(false);
      await fetchBooks();
    } catch (error) {
      console.error("Error al guardar la resolución del libro:", error);
      alert(
        previousImagesRemoved
          ? "No se pudo guardar el libro. Las imágenes anteriores ya se eliminaron."
          : "No se pudo guardar el libro.",
      );
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  const handleDelete = async (book) => {
    if (!window.confirm(`¿Eliminar "${book.name}" y todas sus imágenes?`)) {
      return;
    }

    try {
      setSaving(true);
      setProgress(`Eliminando ${book.name}...`);
      await removeBookFiles(book.name);
      await fetchBooks();
    } catch (error) {
      console.error("Error al eliminar la resolución del libro:", error);
      alert("No se pudo eliminar el libro.");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="min-h-screen bg-gray-100 p-4">
          <div className="w-full rounded-lg bg-white p-6 shadow-md">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-gray-800">
                Resolución de libros
              </h2>
              <button
                onClick={openCreateForm}
                disabled={saving}
                className="rounded-lg bg-blue-500 px-6 py-2 font-medium text-white shadow-md hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Agregar nuevo libro
              </button>
            </div>

            {showForm && (
              <form
                onSubmit={handleSave}
                className="mb-6 rounded-lg border border-gray-200 bg-slate-50 p-6"
              >
                <h3 className="mb-4 text-lg font-semibold text-gray-800">
                  {editingBook ? "Reemplazar libro" : "Nuevo libro"}
                </h3>
                {editingBook && (
                  <p className="mb-4 text-sm text-gray-600">
                    El PDF nuevo reemplazará completamente la portada y todas
                    las páginas actuales.
                  </p>
                )}
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Nombre del libro
                  </label>
                  <input
                    type="text"
                    value={bookName}
                    onChange={(event) => setBookName(event.target.value)}
                    disabled={saving}
                    className="block w-full rounded-lg border px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Archivo PDF
                  </label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handlePdfChange}
                    disabled={saving}
                    className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 disabled:bg-gray-100"
                    required
                  />
                </div>
                {totalPages > 0 && (
                  <div className="mb-4">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Páginas a tomar de las {totalPages} detectadas
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={selectedPages}
                      onChange={(event) => setSelectedPages(event.target.value)}
                      disabled={saving}
                      className="block w-full rounded-lg border px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
                      required
                    />
                  </div>
                )}
                {progress && (
                  <p className="mb-4 text-sm text-blue-700">{progress}</p>
                )}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving || !pdfFile || !totalPages}
                    className="rounded-lg bg-green-600 px-6 py-2 font-medium text-white shadow-md hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving
                      ? "Procesando..."
                      : editingBook
                        ? "Reemplazar libro"
                        : "Crear libro"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    disabled={saving}
                    className="rounded-lg bg-gray-500 px-6 py-2 font-medium text-white shadow-md hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {loading ? (
              <p className="py-10 text-center text-gray-600">
                Cargando libros...
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full overflow-hidden rounded-lg border border-gray-200 shadow-lg">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                        Portada
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                        Páginas
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {books.map((book) => (
                      <tr
                        key={book.name}
                        className="transition hover:bg-gray-100"
                      >
                        <td className="px-6 py-4">
                          <img
                            src={book.imageUrl}
                            alt={book.name}
                            className="h-20 w-16 rounded-lg border border-gray-300 object-cover shadow-sm"
                          />
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-800">
                          {book.name}
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {book.pageCount}
                        </td>
                        <td className="flex gap-2 px-6 py-4">
                          <button
                            onClick={() => openEditForm(book)}
                            disabled={saving}
                            className="rounded-md bg-yellow-500 px-4 py-2 text-white shadow transition hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(book)}
                            disabled={saving}
                            className="rounded-md bg-red-500 px-4 py-2 text-white shadow transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!books.length && (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-10 text-center text-gray-500"
                        >
                          No hay libros cargados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AuthInterceptor>
    </Layout>
  );
}
